import { stores } from '@kurtosys/app-start';
import { models as eventManagerModels } from '@kurtosys/event-manager';
import { IManifest } from '@kurtosys/types/appsManager/index.js';
import { IGlobalInputsChangeDetail } from '@kurtosys/types/eventBus/IGlobalInputsChangeDetail.js';
import { StorageType } from '@kurtosys/types/eventBus/StorageType.js';
import { action, computed, observable, toJS } from 'mobx';

import { EventBusDetail } from '../../../models/app/EventBusDetail.js';
import { IComponentStyles } from '../../../models/app/IComponentStyles.js';
import { IConfiguration } from '../../../models/app/IConfiguration.js';
import { TStoreContext } from '../../../models/app/TStoreContext.js';
import { DEFAULT_EVENT_BUS_ID } from '../../shared/constants.js';

import { AppStore } from './AppStore';

export type TGlobalInputValues = { [key: string]: any };

export class GlobalInputsStore extends stores.base.StoreBase<IConfiguration, IComponentStyles> {
	@observable values: TGlobalInputValues = {};
	@observable filterValues: any = {};

	constructor(storeContext: TStoreContext, manifest: IManifest) {
		super(storeContext, manifest);
	}

	@action
	async initialize(): Promise<void> {
		this.writeDebug('initialize');
		const appStore = this.storeContext.get<AppStore>('appStore');
		if (appStore.loadInitialValuesFromStorage) {
			if (this.storageType === StorageType.Local) {
				const localStorageValues = localStorage.getItem(this.storageKey);
				this.values = (localStorageValues && JSON.parse(localStorageValues)) || {};
			}
			if (this.storageType === StorageType.Session) {
				const sessionStorageValues = sessionStorage.getItem(this.storageKey);
				this.values = (sessionStorageValues && JSON.parse(sessionStorageValues)) || {};
			}
		}
	}

	@action
	writeDebug(action: string, detail?: any) {
		this.storeContext.get<AppStore>('appStore').log('debug', {
			message: action,
			additionalContext: 'GlobalInputsStore',
			detail
		});
	}

	/**
	 * indicates what storage type to use for this inputs app ~ StorageType.Default = Session.
	 *
	 * @readonly
	 */
	@computed
	get storageType(): StorageType {
		const appStore = this.storeContext.get<AppStore>('appStore');
		return (appStore.storage && appStore.storage.type) || StorageType.Default;
	}

	@computed
	get storageKey(): string {
		const appStore = this.storeContext.get<AppStore>('appStore');
		const inputStore = this.storeContext.get<stores.InputStore<IConfiguration, IComponentStyles>>('inputStore');
		if (appStore) {
			return inputStore.getStorageId(appStore.eventBusIdentifier);
		}
		return inputStore.getStorageId(DEFAULT_EVENT_BUS_ID);
	}

	getValue(key: string) {
		this.writeDebug('getValue', {
			key,
			value: this.values[key],
		});
		return this.values[key];
	}

	@action
	setValue(key: string, value: any, notifyEventBus = true) {
		const oldValue = this.values[key];
		this.writeDebug('setValue', {
			key,
			oldValue,
			value,
		});
		this.storeValue(key, value);
		if (notifyEventBus) {
			this.notifyEventBus();
		}
	}

	@action
	setFilterValues(value: any) {
		const oldValue = this.filterValues;
		this.writeDebug('setFilterValues', {
			oldValue,
			value,
		});
		this.filterValues = value;
		this.notifyEventBus();
	}

	@action
	storeValue(key: string, value: any) {
		this.writeDebug('storeValue', {
			key,
			value,
			storageType: this.storageType,
		});
		this.values = {
			...this.values,
			[key]: value,
		};
		if (this.storageType === StorageType.Local) {
			localStorage.setItem(this.storageKey, JSON.stringify(this.values));
		} else if (this.storageType === StorageType.Session) {
			sessionStorage.setItem(this.storageKey, JSON.stringify(this.values));
		}
	}

	@action
	notifyEventBus() {
		const eventBusStore =
			this.storeContext.get<stores.EventBusStore<IConfiguration, IComponentStyles>>('eventBusStore');
		const detail = this.eventBusDetail;
		this.writeDebug('notifyEventBus', detail);
		eventBusStore.trigger({
			type: eventManagerModels.EventTypes.GLOBAL_INPUTS_CHANGE,
			...detail,
		});
	}

	@computed
	get eventBusDetail(): IGlobalInputsChangeDetail {
		const appStore = this.storeContext.get<AppStore>('appStore');
		const detail: IGlobalInputsChangeDetail = {};
		if (appStore) {
			detail.identifier = appStore.eventBusIdentifier;
			detail.storageType = this.storageType;
			if (appStore.eventBusInclude.includes(EventBusDetail.Values)) {
				detail.values = toJS(this.values);
				detail.filterValues = toJS(this.filterValues);
			}
		}
		return detail;
	}
}
